import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { CommonModule, DOCUMENT, isPlatformBrowser } from "@angular/common";
import {
    Component,
    HostListener,
    Inject,
    OnChanges,
    OnDestroy,
    OnInit,
    PLATFORM_ID,
    Renderer2,
    SimpleChanges,
    ViewEncapsulation,
} from "@angular/core";
import { Title } from "@angular/platform-browser";
import {
    ActivatedRoute,
    Event,
    NavigationCancel,
    NavigationEnd,
    NavigationError,
    NavigationStart,
    Router,
    RouterModule,
} from "@angular/router";
import {
    Client,
    ComponentActionTypes,
    Job,
    Page,
    PageComponentTypes,
    Template,
    TemplateRow,
    TriggeredComponentAction,
    VoyseComponent,
} from "@interfaces";
import { ReplaySubject, Subscription, debounce, tap, timer } from "rxjs";
import { JsonLdComponent } from "../containers/json-ld/json-ld.component";
import { TagManagerContainerComponent } from "../containers/tag-manager-container/tag-manager-container.component";
import { TypefaceLoaderComponent } from "../containers/typefaces/typeface-loader/typeface-loader.component";
import { getDesktopColSpan } from "../helpers/tailwind";
import { ComponentOverlayComponent } from "../page-components/component-overlay/component-overlay.component";
import { ComponentResolverComponent } from "../page-components/component-resolver/component-resolver.component";
import { FooterComponent } from "../page-components/footer/footer.component";
import { MenuComponent } from "../page-components/menu/menu.component";
import { SubdomainService } from "../rest/subdomain.service";
import { convertJobAddress, convertJobType, convertSalaryUnits } from "../routes/jobs/job-helpers";
import { ComponentActionsService } from "../services/component-actions.service";
import { FaviconService } from "../services/favicon.service";
import { MetadataService } from "../services/meta-service.service";
import { ResizeService } from "../services/resize.service";
import { DummyJob, DummyPage } from "./dummy-data";
import { TemplateRowComponent } from "./template-row/template-row.component";

@Component({
    selector: "app-renderer",
    templateUrl: "./renderer.component.html",
    styleUrls: ["./renderer.component.scss"],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        JsonLdComponent,
        FooterComponent,
        MenuComponent,
        TagManagerContainerComponent,
        ComponentOverlayComponent,
        TypefaceLoaderComponent,
        ComponentResolverComponent,
        TemplateRowComponent,
    ],
})
export class RendererComponent implements OnInit, OnChanges, OnDestroy {
    public _entity = new ReplaySubject<Page | Job>();
    public readonly entity$ = this._entity.asObservable();
    public editMode = false;
    public templateLocked = false;
    public noTracking = false;
    public mobileMode = false;
    public mobilePreview = false;
    public loading = true;
    public client: Client;
    public rendererStyles = {};
    public jobStructuredData = {};
    public pageStructuredData = {};
    private resizeSubscription: Subscription;

    // Cache for expensive calculations
    private lastEntityHash: string;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        protected breakpointObserver: BreakpointObserver,
        private favicon: FaviconService,
        private subdomain: SubdomainService,
        private meta: MetadataService,
        private actions: ComponentActionsService,
        private title: Title,
        @Inject(PLATFORM_ID) private platform,
        @Inject(DOCUMENT) private document,
        private renderer2: Renderer2,
        private resize: ResizeService
    ) {
        this.breakpointObserver
            .observe([
                Breakpoints.HandsetPortrait,
                Breakpoints.TabletPortrait,
                Breakpoints.HandsetLandscape,
                Breakpoints.TabletLandscape,
                Breakpoints.Medium,
                Breakpoints.Large,
                Breakpoints.XLarge,
            ])
            .subscribe((result) => {
                this.setLayoutMode();
            });

        this.router.events
            .pipe(
                tap((event: Event) => {
                    switch (true) {
                        case event instanceof NavigationStart:
                            this.loading = true;
                            break;
                        case event instanceof NavigationEnd:
                        case event instanceof NavigationError:
                        case event instanceof NavigationCancel:
                            this.loading = false;
                            this.setupFavicon();
                            break;
                        default:
                            break;
                    }
                })
            )
            .subscribe();

        if (isPlatformBrowser(this.platform)) {
            this.resizeSubscription = this.resize.getResizeEvent()?.subscribe((event) => {
                this.setLayoutMode();
            });
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        this.setLayoutMode();
    }

    ngOnInit(): void {
        this.setLayoutMode();

        this.actions.componentActionTriggered
            .pipe(debounce(() => timer(200)))
            .subscribe((value: TriggeredComponentAction) => {
                console.debug("RENDERER SENDING ACTION", value);
                window.parent.postMessage(value, "*");
            });

        this.subdomain.getClient().subscribe((client) => {
            this.client = client;
            this.entity$.subscribe((entity) => {
                this.calculateRendererStyles(entity);
                this.setBodyBackgroundColor(entity);
            });
        });

        this.route.data.subscribe(({ editMode, page, job, template }) => {
            // EDITOR MODE
            if (editMode && isPlatformBrowser(this.platform)) {
                this.noTracking = true;
                this.editMode = true;
                window["ga-disable-GA_MEASUREMENT_ID"] = true;
                this.actions.triggerComponentAction({ action: ComponentActionTypes.IFRAME_LOADED });
            }

            // LIVE MODE
            if (page || job) {
                this.loading = false;
                this._entity.next(page || job);
            }

            if (job && !this.editMode) {
                this.createJobStructuredData(job);
            }

            if (page && !this.editMode) {
                this.createPageStructuredData(page, template);
            }

            // SCREENSHOT MODE
            if (template) {
                this.loadTemplate(template);
            }
        });
    }

    @HostListener("window:message", ["$event"])
    onMessage(e) {
        if (!e.data.action) return;
        console.debug("RENDERER RECEIVED ACTION", e.data);
        this.actions.receiveComponentAction(e.data);
        switch (e.data.action) {
            case ComponentActionTypes.ROW_SELECTED:
                if (Object(e.data).hasOwnProperty("rowIndex")) {
                    this.scrollToRow(e.data.rowIndex);
                }
                break;
            case ComponentActionTypes.COMPONENT_SELECTED:
                if (Object(e.data).hasOwnProperty("index")) {
                    this.scrollToComponent(e.data.index);
                }
                break;
            case ComponentActionTypes.GRID_COMPONENT_SELECTED:
                if (Object(e.data).hasOwnProperty("gridIndex")) {
                    this.scrollToGridComponent(e.data.gridIndex);
                }
                break;
            case ComponentActionTypes.DESKTOP_VIEW_SELECTED:
            case ComponentActionTypes.MOBILE_VIEW_SELECTED:
                this.setLayoutMode();
                this.loadTemplate(e.data.templateData);
                break;
            case ComponentActionTypes.TEMPLATE_LOADED:
            case ComponentActionTypes.TEMPLATE_UPDATED:
            case ComponentActionTypes.COMPONENT_UPDATED:
            case ComponentActionTypes.GRID_COMPONENT_UPDATED:
            case ComponentActionTypes.ITEM_UPDATED:
            case ComponentActionTypes.ROW_UPDATED:
            case ComponentActionTypes.ROW_DELETED:
            case ComponentActionTypes.COMPONENT_DELETED:
            case ComponentActionTypes.GRID_COMPONENT_DELETED:
            case ComponentActionTypes.ITEM_DELETED:
                if (e.data.templateData) {
                    this.loadTemplate(e.data.templateData);
                }
                break;
            case ComponentActionTypes.THEME_UPDATED:
                if (e.data.themeData) {
                    this.subdomain.setClientPreview(e.data.themeData);
                }
                if (e.data.templateData) {
                    this.loadTemplate(e.data.templateData);
                }
                break;
        }
    }

    public calculateRendererStyles(entity: Job | Page) {
        const entityHash = this.createEntityHash(entity);

        if (entityHash === this.lastEntityHash) {
            return; // Skip recalculation if entity hasn't changed
        }

        this.rendererStyles = {
            "--bodyFont": `"${entity.bodyFont || "Inter"}"`,
            "--headingFont": `"${entity.headingFont || "Inter"}"`,
            "--textColor": entity.textColour ?? (entity.darkMode ? "#f3f4f6" : "#111827"),
        };

        this.lastEntityHash = entityHash;
    }

    private createEntityHash(entity: Job | Page): string {
        if (!entity) return '';

        return JSON.stringify({
            bodyFont: entity.bodyFont,
            headingFont: entity.headingFont,
            textColour: entity.textColour,
            darkMode: entity.darkMode,
            bgColor: entity.bgColor
        });
    }

    private setBodyBackgroundColor(entity: Job | Page) {
        const backgroundColor = entity?.bgColor || (entity.darkMode ? "#030712" : "#FFFFFF");
        this.renderer2.setStyle(this.document.body, "background-color", backgroundColor);
    }

    public fetchComponentClasses(component: VoyseComponent, row: TemplateRow) {
        return [getDesktopColSpan(component.colspan, row.components?.length), "col-auto"];
    }

    // TrackBy functions for better performance in ngFor loops
    public trackByRow(index: number, row: TemplateRow): any {
        return row.elementId || index;
    }

    public trackByComponent(index: number, component: VoyseComponent): any {
        return `${component.type}-${component.componentIndex}-${component.rowIndex}` || index;
    }

    public setLayoutMode() {
        const mobileBreakpoints = [Breakpoints.HandsetPortrait, Breakpoints.TabletPortrait];
        const newMobileMode = this.breakpointObserver.isMatched(mobileBreakpoints) || this.mobilePreview;

        if (newMobileMode !== this.mobileMode) {
            this.mobileMode = newMobileMode;
        }
    }

    private setupFavicon() {
        this.favicon.applyFavicon();
    }

    private scrollToRow(rowIndex: number) {
        const rowId = `template-row-${rowIndex}`;
        const element = document.getElementById(rowId);
        element?.scrollIntoView({ behavior: "smooth" });
    }

    private scrollToComponent(componentIndex: number) {
        const componentId = `component-${componentIndex}`;
        const element = document.getElementById(componentId);
        element?.scrollIntoView({ behavior: "smooth" });
    }

    private scrollToGridComponent(gridComponentIndex: number) {
        const componentId = `grid-component-${gridComponentIndex}`;
        const element = document.getElementById(componentId);
        element?.scrollIntoView({ behavior: "smooth" });
    }

    public loadTemplate(template: Template) {
        if (!template) return;
        this.templateLocked = template.locked;
        if (template?.type !== "page") {
            this._entity.next({
                ...template,
                ...DummyJob,
            } as unknown as Job);
        } else {
            this._entity.next({
                ...template,
                ...DummyPage,
            } as unknown as Page);
        }
    }

    public createJobStructuredData(job: Job): void {
        if (!this.client.showOnGoogleJobs) {
            return;
        }
        const createdAt = new Date(job.createdAt);
        const deadline = job.deadline ? new Date(job.deadline) : null;
        this.jobStructuredData = {
            "@context": "https://schema.org",
            "@type": "JobPosting",
            url: job.permalink,
            title: job.title,
            description: job.content.en,
            datePosted: createdAt.toISOString(),
            directApply: !job.customApplyURL,
            validThrough:
                deadline?.toISOString() ||
                (createdAt.setFullYear(createdAt.getFullYear() + 1) && createdAt.toISOString()),
            employmentType: convertJobType(job.type),
            hiringOrganization: {
                "@type": "Organization",
                name: this.client?.name || "Voyse",
                sameAs: this.client?.website || "https://voyse.io",
                logo: `https://res.cloudinary.com/minirobot/image/upload/f_auto,q_auto,c_pad,h_150,w_150/v1/${this.client?.logo?.public_id}`,
            },
            jobLocation: {
                "@type": "Place",
                address: convertJobAddress(job.locationMeta),
            },
            baseSalary: {
                "@type": "MonetaryAmount",
                currency: "GBP",
                value: {
                    "@type": "QuantitativeValue",
                    minValue: job.salary?.from,
                    maxValue: job.salary?.to,
                    unitText: convertSalaryUnits(job.salary?.type),
                },
            },
        };
    }

    public createPageStructuredData(page: Page, template: Template): void {
        // Find component of type "jobBoard" within template rows
        if (
            !template?.rows?.find((row) =>
                row?.components?.find((component) => component?.type === PageComponentTypes.JOB_BOARD)
            )
        ) {
            return;
        }
        // Create structured data for sitelinks search box
        this.pageStructuredData = {
            "@context": "https://schema.org",
            "@type": "WebSite",
            url: page.permalink,
            potentialAction: {
                "@type": "SearchAction",
                target: `${page.permalink}?keywords={search_term_string}`,
                "query-input": "required name=search_term_string",
            },
        };
    }

    ngOnDestroy() {
        this.meta.tearDownMetaTags();
        // Always remember to unsubscribe to prevent memory leaks
        if (this.resizeSubscription) {
            this.resizeSubscription.unsubscribe();
        }
    }
}
